import type { CoreFieldDensity, CoreFieldVariant } from './types'

/**
 * Shared Tailwind class mapping for input-like controls.
 *
 * These classes intentionally include:
 * - explicit border/background/padding (because of the CSS reset)
 * - a stable focus-visible ring
 * - stable variant + density tokens (so unit tests can assert on them)
 */
export function getCoreControlClasses(options: {
  variant: CoreFieldVariant
  density: CoreFieldDensity
  invalid: boolean
}) {
  const variantClasses: Record<CoreFieldVariant, string> = {
    outlined: 'border border-[var(--theme-field-border)] bg-[var(--theme-field-surface)]',
    filled: 'border border-[var(--theme-field-border-muted)] bg-[var(--theme-field-surface-filled)]',
    plain: 'border border-[var(--theme-field-border-muted)] bg-transparent',
  }

  const densityClasses: Record<CoreFieldDensity, string> = {
    compact: 'py-1.5',
    default: 'py-2',
    comfortable: 'py-3',
  }

  return [
    'core-field-control',
    'w-full',
    'min-w-0',
    'px-3',
    'text-sm',
    'text-on-surface',
    'placeholder:text-[color:var(--theme-field-placeholder)]',
    'rounded-ui',
    'transition-default',
    'duration-fast',
    variantClasses[options.variant],
    densityClasses[options.density],
    'disabled:text-[color:var(--theme-field-disabled-text)]',
    'disabled:bg-[var(--theme-field-surface-disabled)]',
    'disabled:border-[var(--theme-field-border-disabled)]',
    'read-only:text-[color:var(--theme-field-readonly-text)]',
    'read-only:bg-[var(--theme-field-surface-readonly)]',
    'read-only:border-[var(--theme-field-border-readonly)]',
    'focus-visible:outline-none',
    'focus-visible:ring-2',
    'focus-visible:ring-[var(--theme-focus-ring)]',
    'focus-visible:ring-offset-1',
    'focus-visible:ring-offset-[var(--theme-focus-ring-offset)]',
    options.invalid ? 'border-danger' : '',
  ].filter(Boolean).join(' ')
}

export function getCoreSliderClasses(options: {
  variant: CoreFieldVariant
  density: CoreFieldDensity
  invalid: boolean
}) {
  const variantClasses: Record<CoreFieldVariant, string> = {
    outlined: 'core-slider--outlined',
    filled: 'core-slider--filled',
    plain: 'core-slider--plain',
  }

  const densityClasses: Record<CoreFieldDensity, string> = {
    compact: 'core-slider--compact',
    default: 'core-slider--default',
    comfortable: 'core-slider--comfortable',
  }

  return [
    'core-slider',
    'w-full',
    'min-w-0',
    'focus-visible:outline-none',
    'focus-visible:ring-2',
    'focus-visible:ring-[var(--theme-focus-ring)]',
    'focus-visible:ring-offset-1',
    'focus-visible:ring-offset-[var(--theme-focus-ring-offset)]',
    variantClasses[options.variant],
    densityClasses[options.density],
    options.invalid ? 'core-slider--invalid' : '',
  ].filter(Boolean).join(' ')
}
